// Devis reçus d'une autre entreprise : liste dans le dossier, import d'un classeur (aperçu contrôlé au centime avant import),
// consultation en lecture seule, comparaison avec nos ouvrages types (heures estimées, sans prix inventé), reconstitution.
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileInput, FileSpreadsheet, FileText, FileUp, Hammer, Trash2, Upload } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { date, dateHeure, euros, pluriel } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Champ, ChampChoix, Chargement, EtatErreur, EtatVide, LienBouton, MarqueIa, Onglets, Panneau, useConfirmation, useNotifier,
} from '../../components/ui.tsx';
import { useSession } from '../../lib/session.tsx';
import { DossierCadre } from '../etudes/DossierCadre.tsx';
import { quantite, useEtroit } from './commun.tsx';

export type ChampColonne = 'ref' | 'des' | 'u' | 'qte' | 'pu' | 'mt';
type Colonnes = Record<ChampColonne, number | null>;
const CHAMPS: ChampColonne[] = ['ref', 'des', 'u', 'qte', 'pu', 'mt'];
export interface LigneRecue {
  id?: string; kind: 'poste' | 'ligne' | 'sous_total'; numero?: number; source_row?: number; poste: string | null; reference: string | null; label: string;
  unit: string | null; quantity: string | null; unit_price: string | null; amount: string | null; computed_amount: string | null; check_ok: boolean | null;
}
export interface Note { numero: number; categorie: 'inclus' | 'exclus' | 'pour_memoire' | 'conditions' | 'autre'; texte: string }
export interface Controle { code: string; libelle: string; numero: number | null; lu: string | null; calcule: string | null; ecart: string | null; ok: boolean | null }
export interface Resume { nbLignesPrix: number; nbPostes: number; nbSousTotaux: number; nbEcarts: number; nbNonVerifiables: number; nbNotes: number }
export interface Analyse {
  feuille: string | null; ligneEntete: number; lignes: LigneRecue[]; notes: Note[]; totalHtLu: string | null; totalHtCalcule: string;
  tvaLue: string | null; tauxTva: string | null; ttcLu: string | null; controles: Controle[]; resume: Resume;
}
interface Apercu {
  feuilles: { nom: string | null; nbLignes: number }[]; feuille: string | null; ligneEntete: number | null; colonnes: Colonnes | null;
  apercuLignes: { numero: number; texte: string }[]; entete: { index: number; lettre: string; texte: string }[];
  libellesColonnes: Record<ChampColonne, string>; meta: { reference: string | null; date: string | null }; analyse: Analyse | null;
}
const LIBELLES_NOTES: Record<Note['categorie'], string> = { inclus: 'Inclus', exclus: 'Exclus', pour_memoire: 'Pour mémoire (non compté)', conditions: 'Conditions', autre: 'Autres mentions' };

export function BadgeControle({ ok }: { ok: boolean | null }) {
  if (ok === true) return <Badge ton="succes">Juste au centime</Badge>;
  if (ok === false) return <Badge ton="danger">Écart</Badge>;
  return <Badge>Non vérifiable</Badge>;
}
const eur = (v: string | null | undefined) => euros(v) ?? '—';

// ═══ Liste dans l'onglet Devis du dossier ═══════════════════════════════════════════════════════
interface DevisRecuListe { id: string; supplier_name: string; supplier_reference: string | null; quote_date: string | null; file_name: string;
  total_ht_declared: string | null; total_ht_computed: string; resume: Resume; created_at: string }

export function PanneauDevisRecus({ dossierId }: { dossierId: string }) {
  const { peut } = useSession();
  const q = useQuery({ queryKey: ['devis-recus', dossierId], queryFn: () => api.get<DevisRecuListe[]>(`/api/dossiers/${dossierId}/devis-recus`) });
  return (
    <Panneau serre titre="Devis reçus d’autres entreprises" actions={peut('devis.ecrire') && (
      <span className="groupe-btn devis-imports-actions">
        <LienBouton to={`/etudes/${dossierId}/devis-recus/import`} icone={<FileInput aria-hidden />}>Importer un tableur</LienBouton>
        <LienBouton to={`/etudes/${dossierId}/devis-pdf/import`} icone={<FileText aria-hidden />}>Importer un PDF</LienBouton>
      </span>)}>
      {q.isLoading && <Chargement lignes={2} />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {q.data && !q.data.length && (
        <EtatVide icone={<FileSpreadsheet />} titre="Aucun devis reçu importé"
          texte="Importez le classeur ou le PDF d’un devis d’une autre entreprise : ses lignes sont contrôlées au centime et comparées à vos ouvrages types. Il reste une référence en lecture seule, jamais émise par vous. Un PDF de commande ou de notre devis signé devient notre devis." />
      )}
      {q.data && !!q.data.length && (
        <div className="liste">
          {q.data.map((d) => (
            <Link key={d.id} to={`/etudes/${dossierId}/devis-recus/${d.id}`} className="devis-revision-lien devis-recu-lien">
              <span className="devis-lien-titre" style={{ minHeight: 0 }}>{d.supplier_name}{d.supplier_reference ? ` — ${d.supplier_reference}` : ''}</span>
              <Badge ton="info">Devis d’un tiers</Badge>
              <span className="chiffre petit">{eur(d.total_ht_declared ?? d.total_ht_computed)} HT</span>
              {d.resume?.nbEcarts ? <Badge ton="danger">{pluriel(d.resume.nbEcarts, 'écart')} au centime</Badge> : <Badge ton="succes">Arithmétique juste</Badge>}
              <span className="tres-petit discret">{d.quote_date ? `Daté du ${date(d.quote_date)} — ` : ''}importé le {date(d.created_at)}</span>
            </Link>
          ))}
        </div>
      )}
    </Panneau>
  );
}

// ═══ Import ═════════════════════════════════════════════════════════════════════════════════════
export function PageImportDevisRecu() {
  const { id = '' } = useParams();
  const naviguer = useNavigate();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [fichier, setFichier] = useState<File | null>(null);
  const [a, setA] = useState<Apercu | null>(null);
  const [feuille, setFeuille] = useState<string | null>(null);
  const [ligne, setLigne] = useState<number | null>(null);
  const [cols, setCols] = useState<Colonnes>({ ref: null, des: null, u: null, qte: null, pu: null, mt: null });
  const [entreprise, setEntreprise] = useState('');
  const [refTiers, setRefTiers] = useState('');
  const [dateDevis, setDateDevis] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const lire = async (reglages: Record<string, unknown> | null) => {
    if (!fichier) return;
    const fd = new FormData();
    if (reglages) fd.append('reglages', JSON.stringify(reglages));
    fd.append('fichier', fichier);
    setEnvoi(true); setErreur(null);
    try {
      const x = await api.envoyer<Apercu>(`/api/dossiers/${id}/devis-recus/apercu`, fd);
      setA(x); setFeuille(x.feuille); setLigne(x.ligneEntete);
      setCols(x.colonnes ?? { ref: null, des: null, u: null, qte: null, pu: null, mt: null });
      if (!reglages) { setRefTiers(x.meta.reference ?? ''); setDateDevis(x.meta.date ?? ''); }
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  const importer = async () => {
    if (!fichier || !a) return;
    const fd = new FormData();
    fd.append('reglages', JSON.stringify({ feuille, ligneEntete: ligne, colonnes: cols }));
    fd.append('entreprise', entreprise); fd.append('referenceTiers', refTiers); fd.append('dateDevis', dateDevis);
    fd.append('fichier', fichier);
    setEnvoi(true); setErreur(null);
    try {
      const r = await api.envoyer<{ id: string }>(`/api/dossiers/${id}/devis-recus`, fd);
      notifier('Devis reçu importé.');
      void qc.invalidateQueries({ queryKey: ['devis-recus', id] });
      naviguer(`/etudes/${id}/devis-recus/${r.id}`);
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  const optionsCols = (a?.entete ?? []).map((c) => ({ valeur: String(c.index), libelle: `${c.lettre}${c.texte ? ` — ${c.texte}` : ''}` }));
  const analyse = a?.analyse ?? null;
  const reglagesModifies = !!a && (feuille !== a.feuille || ligne !== a.ligneEntete || CHAMPS.some((c) => cols[c] !== (a.colonnes?.[c] ?? null)));

  return (
    <DossierCadre dossierId={id} actif="devis" actions={<LienBouton to={`/etudes/${id}/devis`} icone={<FileSpreadsheet aria-hidden />}>Tous les devis</LienBouton>}>
      <div className="pile-l devis-page">
        <Panneau titre="1. Classeur du devis reçu">
          <div className="pile">
            <p className="petit secondaire">Classeur XLSX (toutes les feuilles) ou CSV, 10 Mo au plus. Le devis d’une autre entreprise reste une référence en lecture seule,
              rattachée au dossier : il n’est jamais émis par vous et ses prix ne sont jamais repris dans vos devis.</p>
            <label className="btn devis-fichier">
              <FileUp aria-hidden /> {fichier ? fichier.name : 'Choisir le classeur'}
              <input type="file" accept=".xlsx,.csv,.txt,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" className="devis-fichier-saisie"
                onChange={(e) => { setFichier(e.target.files?.[0] ?? null); setA(null); setErreur(null); }} />
            </label>
            {fichier && !a && <Bouton variante="principal" chargement={envoi} onClick={() => lire(null)}>Lire le classeur</Bouton>}
            {erreur && <Alerte ton="danger">{erreur}</Alerte>}
          </div>
        </Panneau>

        {a && (
          <Panneau titre="2. Feuille, ligne d’en-tête et colonnes">
            <div className="pile">
              <p className="petit secondaire">{a.ligneEntete ? 'Réglages détectés : vérifiez-les, corrigez si besoin puis relisez.' : 'Aucune ligne d’en-tête reconnue : choisissez-la, puis les colonnes.'}</p>
              <div className="grille-2">
                <ChampChoix libelle="Feuille" value={feuille ?? ''} options={a.feuilles.map((f) => ({ valeur: f.nom ?? '', libelle: `${f.nom ?? 'CSV'} (${pluriel(f.nbLignes, 'ligne')})` }))}
                  onChange={(e) => { setFeuille(e.target.value); void lire({ feuille: e.target.value }); }} />
                <ChampChoix libelle="Ligne d’en-tête" vide="Choisir" value={ligne === null ? '' : String(ligne)}
                  options={a.apercuLignes.map((l) => ({ valeur: String(l.numero), libelle: `Ligne ${l.numero} — ${l.texte.slice(0, 70)}` }))}
                  onChange={(e) => { const n = e.target.value ? Number(e.target.value) : null; setLigne(n); if (n) void lire({ feuille, ligneEntete: n }); }} />
              </div>
              {!!a.entete.length && (
                <div className="grille-3">
                  {CHAMPS.map((c) => (
                    <ChampChoix key={c} libelle={a.libellesColonnes[c]} vide={c === 'des' ? 'Choisir' : 'Aucune'} value={cols[c] === null ? '' : String(cols[c])}
                      options={optionsCols} onChange={(e) => setCols({ ...cols, [c]: e.target.value === '' ? null : Number(e.target.value) })} />
                  ))}
                </div>
              )}
              {reglagesModifies && ligne && <div className="groupe-btn"><Bouton variante="principal" chargement={envoi}
                onClick={() => lire({ feuille, ligneEntete: ligne, colonnes: cols })}>Relire avec ces réglages</Bouton></div>}
            </div>
          </Panneau>
        )}

        {analyse && !reglagesModifies && (
          <Panneau titre="3. Aperçu et contrôles avant import">
            <div className="pile">
              <ResumeAnalyse resume={analyse.resume} />
              <TableauControles controles={analyse.controles} />
              <LignesRecues lignes={analyse.lignes} />
              {!!analyse.notes.length && <NotesRecues notes={analyse.notes} />}
              <div className="grille-3">
                <Champ libelle="Entreprise qui a émis le devis" required maxLength={200} value={entreprise} onChange={(e) => setEntreprise(e.target.value)} />
                <Champ libelle="Numéro du devis (chez l’entreprise)" mono maxLength={120} value={refTiers} onChange={(e) => setRefTiers(e.target.value)}
                  aide={a?.meta.reference ? 'Lu dans le classeur — à vérifier' : undefined} />
                <Champ libelle="Date du devis" type="date" value={dateDevis} onChange={(e) => setDateDevis(e.target.value)} aide={a?.meta.date ? 'Lue dans le classeur — à vérifier' : undefined} />
              </div>
              <div className="groupe-btn">
                <Bouton variante="principal" icone={<Upload aria-hidden />} chargement={envoi} disabled={!entreprise.trim() || !analyse.resume.nbLignesPrix} onClick={importer}>
                  {`Importer ${pluriel(analyse.resume.nbLignesPrix, 'ligne de prix', 'lignes de prix')}`}
                </Bouton>
                {!entreprise.trim() && <span className="petit discret">Indiquez l’entreprise qui a émis le devis.</span>}
              </div>
            </div>
          </Panneau>
        )}
      </div>
    </DossierCadre>
  );
}

export function ResumeAnalyse({ resume }: { resume: Resume }) {
  return (
    <div className="rangee" style={{ gap: 8 }}>
      <Badge ton="accent">{pluriel(resume.nbLignesPrix, 'ligne de prix', 'lignes de prix')}</Badge>
      <Badge>{pluriel(resume.nbPostes, 'poste')}</Badge>
      <Badge>{pluriel(resume.nbSousTotaux, 'sous-total', 'sous-totaux')}</Badge>
      {resume.nbEcarts ? <Badge ton="danger">{pluriel(resume.nbEcarts, 'écart au centime', 'écarts au centime')}</Badge> : <Badge ton="succes">Aucun écart au centime</Badge>}
      {!!resume.nbNonVerifiables && <Badge ton="ambre">{pluriel(resume.nbNonVerifiables, 'contrôle impossible', 'contrôles impossibles')}</Badge>}
      {!!resume.nbNotes && <Badge ton="info">{`${pluriel(resume.nbNotes, 'mention écartée', 'mentions écartées')} des prix`}</Badge>}
    </div>
  );
}

export function TableauControles({ controles }: { controles: Controle[] }) {
  return (
    <ul className="devis-recu-controles">
      {controles.map((c, i) => (
        <li key={`${c.code}-${i}`} className="devis-recu-controle">
          <span className="petit">{c.libelle}{c.numero ? <span className="tres-petit discret"> — ligne {c.numero}</span> : null}</span>
          <span className="tres-petit secondaire chiffre">{c.lu !== null ? `lu ${eur(c.lu)}` : ''}{c.calcule !== null ? ` — calculé ${eur(c.calcule)}` : ''}
            {c.ok === false && c.ecart ? ` — écart ${eur(c.ecart)}` : ''}</span>
          <BadgeControle ok={c.ok} />
        </li>
      ))}
    </ul>
  );
}

/** Lignes du devis reçu : tableau sur ordinateur, cartes sur téléphone. */
export function LignesRecues({ lignes }: { lignes: LigneRecue[] }) {
  const etroit = useEtroit(900);
  if (etroit) {
    return (
      <div className="devis-cartes">
        {lignes.map((l, i) => l.kind === 'poste'
          ? <h3 key={i} className="devis-cartes-titre"><span>{l.label}</span></h3>
          : (
            <article key={i} className={`devis-carte ${l.check_ok === false ? 'devis-recu-ecart' : ''}`}>
              <div className="devis-carte-haut">
                <strong className="devis-carte-titre">{l.kind === 'sous_total' ? l.label : <>{l.reference && <span className="mono">{l.reference} </span>}{l.label}</>}</strong>
                <span className="chiffre devis-fort">{eur(l.amount)}</span>
              </div>
              {l.kind === 'ligne' && <span className="petit secondaire chiffre">{l.quantity !== null ? quantite(l.quantity) : '—'} {l.unit ?? ''} × {eur(l.unit_price)}</span>}
              <span className="rangee" style={{ gap: 6 }}><BadgeControle ok={l.check_ok} />
                {l.check_ok === false && <span className="tres-petit devis-motif">calculé {eur(l.computed_amount)}</span>}</span>
            </article>
          ))}
      </div>
    );
  }
  return (
    <div className="tableau-conteneur">
      <table className="tableau devis-recu-tableau">
        <thead><tr>
          <th scope="col" className="num">Ligne</th><th scope="col">Réf.</th><th scope="col">Désignation</th><th scope="col">Unité</th>
          <th scope="col" className="num">Qté</th><th scope="col" className="num">P.U. HT</th><th scope="col" className="num">Montant HT</th><th scope="col">Contrôle</th>
        </tr></thead>
        <tbody>
          {lignes.map((l, i) => l.kind === 'poste'
            ? <tr key={i} className="devis-section"><th scope="colgroup" colSpan={8}>{l.label}</th></tr>
            : (
              <tr key={i} className={l.kind === 'sous_total' ? 'devis-recu-sous-total' : l.check_ok === false ? 'devis-recu-ecart' : undefined}>
                <td className="num tres-petit discret">{l.numero ?? l.source_row}</td>
                <td className="mono petit">{l.reference ?? ''}</td>
                <td>{l.label}</td>
                <td>{l.unit ?? ''}</td>
                <td className="num chiffre">{l.quantity !== null ? quantite(l.quantity) : ''}</td>
                <td className="num chiffre">{l.unit_price !== null ? eur(l.unit_price) : ''}</td>
                <td className="num chiffre devis-fort">{eur(l.amount)}</td>
                <td><BadgeControle ok={l.check_ok} />{l.check_ok === false && <span className="tres-petit devis-motif devis-sous">calculé {eur(l.computed_amount)}</span>}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

export function NotesRecues({ notes }: { notes: Note[] }) {
  const groupes = (['inclus', 'exclus', 'pour_memoire', 'conditions', 'autre'] as const).map((c) => ({ c, n: notes.filter((x) => x.categorie === c) })).filter((g) => g.n.length);
  return (
    <div className="pile">
      <h3 className="ouvrages-sous-titre">Mentions écartées des prix</h3>
      <div className="devis-recu-notes">
        {groupes.map((g) => (
          <section key={g.c} aria-label={LIBELLES_NOTES[g.c]}>
            <h4 className="petit devis-fort">{LIBELLES_NOTES[g.c]}</h4>
            <ul className="devis-liste-simple">{g.n.map((n) => <li key={n.numero} className="petit">{n.texte} <span className="tres-petit discret">(ligne {n.numero})</span></li>)}</ul>
          </section>
        ))}
      </div>
    </div>
  );
}

// ═══ Consultation, comparaison, reconstitution ═════════════════════════════════════════════════
interface DevisRecu {
  id: string; supplier_name: string; supplier_reference: string | null; quote_date: string | null; file_name: string; sheet_name: string | null;
  header_row: number; total_ht_declared: string | null; total_ht_computed: string; vat_declared: string | null; ttc_declared: string | null;
  checks: { controles: Controle[]; resume: Resume; taux_tva: string | null; source?: { format: 'pdf'; lecture: 'texte' | 'ia'; import_pdf_id: string; modele_ia?: string | null } };
  notes: Note[]; created_at: string; created_by_name: string | null;
  lignes: LigneRecue[]; droits: { ecrire: boolean; couts: boolean };
}
interface Comparaison {
  lignes: { id: string; ouvrage: { id: string; code: string; label: string; unit: string } | null; origine: 'regle' | 'choix' | null; quantite: string | null;
    note_quantite: string | null; heures: string | null; motif_sans_heures: string | null; mo_reconstituee: string | null; reste: string | null }[];
  totaux: { heures: string; lignes_rapprochees: number; lignes_sans_ouvrage: number; lignes_sans_heures: number; mo_reconstituee: string | null;
    montant_rapproche: string; reste: string | null };
  contexte: { drv: boolean; t3: boolean; circuits: string | null };
  taux: { code: string; hourly_price: string } | null;
  nb_ouvrages: number;
  ouvrages: { id: string; code: string; label: string; unit: string; category: string }[];
}

export function PageDevisRecu() {
  const { id = '', rid = '' } = useParams();
  const [onglet, setOnglet] = useState<'lignes' | 'comparaison' | 'notes'>(() => {
    const v = new URLSearchParams(window.location.search).get('vue');
    return v === 'comparaison' || v === 'notes' ? v : 'lignes';
  });
  const q = useQuery({ queryKey: ['devis-recu', rid], queryFn: () => api.get<DevisRecu>(`/api/devis-recus/${rid}`) });
  const naviguer = useNavigate();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const { confirmer, element } = useConfirmation();
  const supprimer = async () => {
    if (!q.data || !(await confirmer('Supprimer ce devis reçu ?', `L’import de « ${q.data.file_name} » et son rapprochement seront supprimés. Le fichier d’origine n’est pas touché.`, 'Supprimer l’import'))) return;
    try {
      await api.del(`/api/devis-recus/${rid}`);
      notifier('Import supprimé.'); void qc.invalidateQueries({ queryKey: ['devis-recus', id] }); naviguer(`/etudes/${id}/devis`);
    } catch (e) { notifier(messageErreur(e), true); }
  };
  const reconstituer = useMutation({
    mutationFn: () => api.post<{ quoteId: string; numero: string; lignes: number }>(`/api/devis-recus/${rid}/reconstitution`, {}),
    onSuccess: (r) => { notifier(`Reconstitution ${r.numero} créée en brouillon (${pluriel(r.lignes, 'ligne')}).`); void qc.invalidateQueries({ queryKey: ['devis-dossier', id] });
      naviguer(`/etudes/${id}/devis/${r.quoteId}`); },
    onError: (e) => notifier(messageErreur(e), true),
  });
  const d = q.data;
  return (
    <DossierCadre dossierId={id} actif="devis" large actions={<LienBouton to={`/etudes/${id}/devis`} icone={<FileSpreadsheet aria-hidden />}>Tous les devis</LienBouton>}>
      {q.isLoading && <Chargement lignes={5} />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {d && (
        <div className="pile-l devis-page">
          <section className="panneau devis-barre" aria-label="Devis reçu">
            <div className="devis-barre-haut">
              <div className="pile" style={{ gap: 4, minWidth: 0 }}>
                <h2 className="devis-titre">Devis reçu — {d.supplier_name}{d.supplier_reference ? <> <span className="mono">{d.supplier_reference}</span></> : null}</h2>
                <span className="rangee" style={{ gap: 6 }}>
                  <Badge ton="info">Devis d’une autre entreprise — lecture seule</Badge>
                  {d.quote_date && <span className="petit secondaire">Daté du {date(d.quote_date)}</span>}
                </span>
                <span className="tres-petit discret">{d.checks.source?.format === 'pdf'
                  ? <><Link to={`/etudes/${id}/devis-pdf/${d.checks.source.import_pdf_id}`}>{d.file_name}</Link> (PDF{d.checks.source.lecture === 'ia' ? '' : ', couche texte'})</>
                  : <>{d.file_name}{d.sheet_name ? `, feuille « ${d.sheet_name} »` : ''}, en-tête ligne {d.header_row}</>} — importé le {dateHeure(d.created_at)}{d.created_by_name ? ` par ${d.created_by_name}` : ''}</span>
                {d.checks.source?.lecture === 'ia' && <MarqueIa texte="Lignes lues par l’assistance IA sur un PDF scanné — à vérifier sur le PDF" />}
              </div>
              <dl className="devis-recu-totaux devis-recu-totaux-barre">
                <div><dt>Total HT lu</dt><dd className="chiffre devis-fort">{eur(d.total_ht_declared)}</dd></div>
                <div><dt>Somme des lignes</dt><dd className="chiffre">{eur(d.total_ht_computed)}</dd></div>
                {d.vat_declared && <div><dt>TVA{d.checks.taux_tva ? ` ${d.checks.taux_tva} %` : ''}</dt><dd className="chiffre">{eur(d.vat_declared)}</dd></div>}
                {d.ttc_declared && <div><dt>Total TTC lu</dt><dd className="chiffre">{eur(d.ttc_declared)}</dd></div>}
              </dl>
            </div>
            {d.droits.ecrire && (
              <div className="devis-barre-actions">
                {d.droits.couts && <Bouton variante="principal" icone={<Hammer aria-hidden />} chargement={reconstituer.isPending} onClick={() => reconstituer.mutate()}>
                  Créer notre reconstitution</Bouton>}
                <Bouton variante="danger" icone={<Trash2 aria-hidden />} onClick={supprimer}>Supprimer l’import</Bouton>
              </div>
            )}
          </section>
          <Panneau titre="Contrôles au centime">
            <div className="pile"><ResumeAnalyse resume={d.checks.resume} /><TableauControles controles={d.checks.controles} /></div>
          </Panneau>
          <Onglets libelle="Devis reçu" actif={onglet} changer={setOnglet}
            onglets={[{ id: 'lignes', libelle: 'Lignes' }, { id: 'comparaison', libelle: 'Comparaison' }, { id: 'notes', libelle: `Mentions (${d.notes.length})` }]} />
          {onglet === 'lignes' && <Panneau serre titre={pluriel(d.checks.resume.nbLignesPrix, 'ligne de prix', 'lignes de prix')}><LignesRecues lignes={d.lignes} /></Panneau>}
          {onglet === 'notes' && (
            <Panneau titre="Inclus, exclus et conditions">
              {d.notes.length ? <NotesRecues notes={d.notes} /> : <EtatVide titre="Aucune mention" texte="Le devis ne contient ni rubrique Inclus / Exclus ni conditions." />}
            </Panneau>
          )}
          {onglet === 'comparaison' && <PanneauComparaison d={d} dossierId={id} />}
          {element}
        </div>
      )}
    </DossierCadre>
  );
}

function PanneauComparaison({ d, dossierId }: { d: DevisRecu; dossierId: string }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const naviguer = useNavigate();
  const c = useQuery({ queryKey: ['devis-recu-comparaison', d.id], queryFn: () => api.get<Comparaison>(`/api/devis-recus/${d.id}/comparaison`) });
  const choisir = useMutation({
    mutationFn: ({ lid, valeur }: { lid: string; valeur: string }) => api.patch(`/api/devis-recus/${d.id}/lignes/${lid}`,
      valeur === '__regle' ? { workItemId: null, proposition: true } : { workItemId: valeur || null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devis-recu-comparaison', d.id] }),
    onError: (e) => notifier(messageErreur(e), true),
  });
  const parId = useMemo(() => new Map((c.data?.lignes ?? []).map((x) => [x.id, x])), [c.data]);
  if (c.isLoading) return <Chargement lignes={4} />;
  if (c.isError || !c.data) return <EtatErreur erreur={c.error} reessayer={() => c.refetch()} />;
  const t = c.data.totaux;
  const opts = c.data.ouvrages.map((o) => ({ valeur: o.id, libelle: `${o.label} (${o.unit})` }));
  return (
    <div className="pile-l">
      {!c.data.nb_ouvrages && (
        <Alerte ton="ambre" titre="Aucun ouvrage type dans le catalogue">
          Installez le jeu initial CVC dans <Link to="/etudes/catalogue?onglet=ouvrages">Catalogue de prix › Ouvrages types</Link> pour rapprocher les lignes et estimer les heures.
        </Alerte>
      )}
      <Panneau titre="Reconstitution des heures">
        <div className="pile">
          <dl className="devis-recu-totaux devis-recu-totaux-large">
            <div><dt>Heures estimées</dt><dd className="chiffre devis-fort">{quantite(t.heures)} h</dd></div>
            <div><dt>Lignes rapprochées</dt><dd className="chiffre">{t.lignes_rapprochees}</dd></div>
            <div><dt>Sans ouvrage type</dt><dd className="chiffre">{t.lignes_sans_ouvrage}</dd></div>
            <div><dt>Sans heures calculables</dt><dd className="chiffre">{t.lignes_sans_heures}</dd></div>
            {t.mo_reconstituee !== null && <div><dt>Main-d’œuvre reconstituée ({c.data.taux?.code}, {eur(c.data.taux?.hourly_price)} / h)</dt><dd className="chiffre">{eur(t.mo_reconstituee)}</dd></div>}
            {t.reste !== null && <div><dt>Reste pour fournitures, frais et marge (lignes avec heures)</dt><dd className="chiffre">{eur(t.reste)}</dd></div>}
          </dl>
          <p className="tres-petit discret">
            Heures = quantité du devis reçu × heures de l’ouvrage type (composants de base). Les propositions d’ouvrage sont à confirmer.
            {c.data.contexte.drv ? ` Devis lu comme un DRV/VRV${c.data.contexte.t3 ? ' 3 tubes' : ''}.` : ''}
            {t.mo_reconstituee === null && d.droits.couts ? ' Pas de valorisation : renseignez un taux de vente indicatif dans le catalogue.' : ''}
            {' '}Aucun prix n’est inventé ; la reconstitution crée un devis brouillon dont les prix viennent de votre catalogue ou restent « À renseigner ».
          </p>
        </div>
      </Panneau>
      <Panneau serre titre="Ligne par ligne">
        <ul className="devis-recu-comparaison">
          {d.lignes.filter((l) => l.kind === 'ligne').map((l) => {
            const x = parId.get(l.id!);
            return (
              <li key={l.id} className="devis-recu-cmp">
                <div className="pile" style={{ gap: 2, minWidth: 0 }}>
                  <span className="petit">{l.reference && <span className="mono">{l.reference} </span>}{l.label}</span>
                  <span className="tres-petit secondaire chiffre">{l.quantity !== null ? quantite(l.quantity) : '—'} {l.unit ?? ''} — {eur(l.amount ?? l.computed_amount)} HT</span>
                </div>
                <div className="devis-recu-cmp-ouvrage">
                  {d.droits.ecrire
                    ? <ChampChoix libelle="Ouvrage type" value={x?.origine === 'choix' ? (x.ouvrage?.id ?? '') : '__regle'}
                        onChange={(e) => choisir.mutate({ lid: l.id!, valeur: e.target.value })}
                        options={[{ valeur: '__regle', libelle: x?.origine !== 'choix' && x?.ouvrage ? `Proposé : ${x.ouvrage.label}` : 'Proposition de la règle (aucune)' },
                          { valeur: '', libelle: 'Aucun ouvrage' }, ...opts]} />
                    : <span className="petit">{x?.ouvrage ? x.ouvrage.label : 'Aucun ouvrage'}</span>}
                </div>
                <div className="devis-recu-cmp-heures">
                  {x?.heures !== null && x?.heures !== undefined
                    ? <span className="chiffre devis-fort">{quantite(x.heures)} h</span>
                    : <span className="tres-petit devis-motif">{x?.ouvrage ? x.motif_sans_heures : '—'}</span>}
                  {x?.note_quantite && <span className="tres-petit discret">{x.note_quantite}</span>}
                  {x?.mo_reconstituee && <span className="tres-petit secondaire">MO {eur(x.mo_reconstituee)} — reste {eur(x.reste)}</span>}
                  {x?.origine === 'regle' && x.ouvrage && <span className="tres-petit discret"><CheckCircle2 size={12} aria-hidden style={{ verticalAlign: '-2px' }} /> proposé, à confirmer</span>}
                </div>
              </li>
            );
          })}
        </ul>
      </Panneau>
    </div>
  );
}
